import {
  COMPOSER_DRAFT_STORAGE_KEY,
  flushComposerDraftPersistence,
  readPersistedComposerFileAttachmentIds,
  useComposerDraftStore,
} from "./composerDraftStore";
import {
  composerFileAttachmentStore,
  type BrowserFileAttachmentStore,
} from "./composerFileAttachmentStore";
import {
  PENDING_USER_INPUT_DRAFT_STORAGE_PREFIX,
  readPersistedPendingUserInputFileAttachmentIds,
} from "./pendingUserInputDraftStore";
import {
  PROMPT_STASH_STORAGE_KEY,
  promptStashStore,
  readPersistedPromptStashFileAttachmentIds,
} from "./promptStashStore";

const ORPHAN_GRACE_MS = 60_000;
const crossTabConflictIds = new Set<string>();

function persistedOwnerIds(storage: Storage): Set<string> | null {
  try {
    const ids = new Set<string>();
    const ownerGroups = [
      readPersistedComposerFileAttachmentIds(storage.getItem(COMPOSER_DRAFT_STORAGE_KEY)),
      readPersistedPromptStashFileAttachmentIds(storage.getItem(PROMPT_STASH_STORAGE_KEY)),
    ];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key?.startsWith(PENDING_USER_INPUT_DRAFT_STORAGE_PREFIX)) continue;
      ownerGroups.push(readPersistedPendingUserInputFileAttachmentIds(storage.getItem(key)));
    }
    for (const owners of ownerGroups) {
      if (owners === null) return null;
      for (const id of owners) ids.add(id);
    }
    return ids;
  } catch {
    return null;
  }
}

function ownedIds(): Set<string> | null {
  const persistedBeforeFlush = readPersistedOwners();
  if (persistedBeforeFlush === null) return null;
  flushComposerDraftPersistence();
  const ids = inMemoryOwnerIds();
  for (const id of persistedBeforeFlush) ids.add(id);
  const persistedAfterFlush = readPersistedOwners();
  if (persistedAfterFlush === null) return null;
  retainChangedPersistedOwnership(persistedBeforeFlush, persistedAfterFlush);
  for (const id of persistedAfterFlush) ids.add(id);
  return ids;
}

function retainChangedPersistedOwnership(before: ReadonlySet<string>, after: ReadonlySet<string>) {
  for (const id of before) {
    if (!after.has(id)) crossTabConflictIds.add(id);
  }
  for (const id of after) {
    if (!before.has(id)) crossTabConflictIds.add(id);
  }
}

function readPersistedOwners(): Set<string> | null {
  if (typeof localStorage === "undefined") return new Set();
  return persistedOwnerIds(localStorage);
}

function inMemoryOwnerIds(): Set<string> {
  const ids = new Set(crossTabConflictIds);
  for (const draft of Object.values(useComposerDraftStore.getState().draftsByThreadKey)) {
    addOwnerIds(ids, draft.files);
    addOwnerIds(ids, draft.stagedSubmission?.files);
  }
  for (const entry of promptStashStore.getState().entries) {
    addOwnerIds(ids, entry.files);
  }
  return ids;
}

function addOwnerIds(ids: Set<string>, files: ReadonlyArray<{ readonly id: string }> | undefined) {
  for (const file of files ?? []) ids.add(file.id);
}

/** Deletes only after two owner snapshots agree that no live draft, staged send, or stash references it. */
export async function releaseUnreferencedComposerFile(id: string): Promise<void> {
  try {
    if (ownedIds()?.has(id) !== false) return;
    await Promise.resolve();
    if (ownedIds()?.has(id) !== false) return;
    await composerFileAttachmentStore.delete(id);
  } catch {
    // A later sweep retries cleanup. Submission success must not depend on local storage cleanup.
  }
}

/** Removes only old bytes that remain unowned across two complete ownership snapshots. */
export async function sweepUnreferencedComposerFiles(
  input: {
    readonly now?: number;
    readonly graceMs?: number;
    readonly store?: Pick<BrowserFileAttachmentStore, "list" | "delete">;
  } = {},
): Promise<void> {
  try {
    const now = input.now ?? Date.now();
    const graceMs = input.graceMs ?? ORPHAN_GRACE_MS;
    const store = input.store ?? composerFileAttachmentStore;
    const firstOwners = ownedIds();
    if (firstOwners === null) return;
    const candidates = (await store.list()).filter(
      (attachment) =>
        attachment.createdAt <= now - graceMs &&
        !firstOwners.has(attachment.id) &&
        !crossTabConflictIds.has(attachment.id),
    );
    if (candidates.length === 0) return;
    await Promise.resolve();
    const secondOwners = ownedIds();
    if (secondOwners === null) return;
    await Promise.all(
      candidates
        .filter(
          (attachment) =>
            !secondOwners.has(attachment.id) && !crossTabConflictIds.has(attachment.id),
        )
        .map((attachment) => store.delete(attachment.id)),
    );
  } catch {
    // A later hydration sweep retries transient IndexedDB failures.
  }
}
